import { Download } from "lucide-react";
import {
  Bar,
  ComposedChart,
  CartesianGrid,
  Legend,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type LabelProps,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Hint } from "@/components/ui/hint";
import { Select } from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "@/components/dashboards/dashboard-table";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import { formatCurrency, formatMonth } from "./shared/formatters";
import { forecastColor } from "./forecast-chart";
import {
  breakdownChartData,
  DIMENSIONS,
  MEASURES,
  measureValue,
  VIEW_DEFINITION,
  LABEL_DEFINITION,
  PERIOD_DEFINITION,
  type BreakdownRow,
  type Dimension,
  type Forecast,
  type Measure,
} from "./forecast-analysis";

export function Definition({ text }: { text: string }) {
  return (
    <Hint text={text} decorate={false}>
      <span
        aria-label="Definisjon"
        className="inline-flex text-xs text-admin-text-subtle"
      >
        (i)
      </span>
    </Hint>
  );
}

const axisStyle = { fontSize: 11, fill: "var(--color-admin-text-subtle)" };
const decimal = (value: number | null) =>
  value === null ? "" : value.toFixed(2).replace(".", ",");

export function ForecastBreakdown({
  forecast,
  dimension,
  measure,
  view,
  mode,
  onMeasure,
  onView,
  onMode,
  onDimension,
}: {
  forecast: Forecast;
  dimension: Dimension;
  measure: Measure;
  view: string;
  mode: string;
  onMeasure: (value: string) => void;
  onView: (value: string) => void;
  onMode: (value: string) => void;
  onDimension?: (value: string) => void;
}) {
  const id = onDimension ? "forecast-customer" : "forecast-product";
  const percent = view === "percent" && measure !== "average";
  const { rows, segments, data } = breakdownChartData(
    forecast,
    dimension,
    measure,
    percent,
  );
  const columns: DashboardTableColumn<BreakdownRow>[] = [
    { id: "month", label: "Måned", value: (row) => row.month },
    {
      id: "segment",
      label: DIMENSIONS[dimension].label,
      value: (row) => row.label,
    },
    ...(["income", "invoices", "customers", "average"] as const).map(
      (metric) => ({
        id: metric,
        // Sortable headers render inside a <button>; a focusable hint there would also sort.
        label: MEASURES[metric].label,
        value: (row: BreakdownRow) => measureValue(row, metric),
        numeric: true,
        align: "right" as const,
      }),
    ),
  ];
  const table = useDashboardTable(id, columns, rows);
  const exportCsv = () => {
    const exportRows = mode === "table" ? table.viewRows : rows;
    downloadCsv(
      toCsv(
        [
          "Måned",
          DIMENSIONS[dimension].label,
          "Forventet inntekt (kr)",
          "Fakturaer",
          "Unike kunder",
          "Inntekt per kunde (kr)",
          "Ukoblede fakturaer",
        ],
        [
          ...exportRows.map((row) => [
            row.month,
            row.label,
            decimal(row.totalAmount / 100),
            row.invoiceCount,
            row.customerCount,
            decimal(measureValue(row, "average")),
            row.unmatchedInvoiceCount,
          ]),
          ...forecast.months
            .filter((month) => !rows.some((row) => row.month === month.month))
            .map((month) =>
              month.available
                ? [month.month, "Ingen fakturaer", "0,00", 0, 0, "", 0]
                : [month.month, "Mangler data", "", "", "", "", ""],
            ),
        ],
        ";",
        { neutralizeFormulas: true },
      ),
      `${id}-${dimension}-${forecast.months[0]?.month}-${forecast.months.at(-1)?.month}.csv`,
      { bom: true },
    );
  };
  const formatValue = (value: number) =>
    percent
      ? `${value.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} %`
      : measure === "income" || measure === "average"
        ? formatCurrency(value)
        : value.toLocaleString("nb-NO");
  const formatTotal = (value: number) =>
    measure === "income"
      ? formatCurrency(value)
      : value.toLocaleString("nb-NO");
  const renderSegmentLabel = ({ value, viewBox }: LabelProps) => {
    if (
      typeof value !== "number" ||
      value === 0 ||
      !viewBox ||
      !("x" in viewBox)
    )
      return null;
    const x = Number(viewBox.x) + Number(viewBox.width) / 2;
    const y =
      measure === "average"
        ? Number(viewBox.y) - 8
        : Number(viewBox.y) + Number(viewBox.height) / 2;
    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fill="var(--color-admin-text-primary)"
        stroke="var(--color-admin-card)"
        strokeWidth={2}
        paintOrder="stroke"
      >
        {formatValue(value)}
      </text>
    );
  };
  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">
            {onDimension
              ? "Forventet inntekt og fordeling per kundegruppe"
              : "Forventet inntekt og fordeling per abonnement"}{" "}
            <Definition text={DIMENSIONS[dimension].definition} />
          </CardTitle>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <p className="text-right text-xs text-admin-text-subtle">
              Totalt for perioden:{" "}
              {formatCurrency(forecast.total.totalAmount / 100)} (
              {forecast.total.invoiceCount} fakturaer){" "}
              <Definition text={PERIOD_DEFINITION} />
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!rows.length}
            >
              <Download className="h-3 w-3" />
              Eksporter CSV
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {onDimension && (
            <div className="space-y-1">
              <label
                htmlFor={`${id}-dimension`}
                className="text-xs text-admin-text-subtle"
              >
                Fordeling <Definition text={DIMENSIONS[dimension].definition} />
              </label>
              <Select
                id={`${id}-dimension`}
                value={dimension}
                onChange={(event) => onDimension(event.target.value)}
              >
                {(["source", "age", "gender", "tenure"] as const).map((key) => (
                  <option key={key} value={key}>
                    {DIMENSIONS[key].label}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <label
              htmlFor={`${id}-measure`}
              className="text-xs text-admin-text-subtle"
            >
              Måltall <Definition text={MEASURES[measure].definition} />
            </label>
            <Select
              id={`${id}-measure`}
              value={measure}
              onChange={(event) => onMeasure(event.target.value)}
            >
              {Object.entries(MEASURES).map(([key, metric]) => (
                <option key={key} value={key}>
                  {metric.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <label
              htmlFor={`${id}-view`}
              className="text-xs text-admin-text-subtle"
            >
              Visning <Definition text={VIEW_DEFINITION} />
            </label>
            <Select
              id={`${id}-view`}
              value={percent ? "percent" : "absolute"}
              disabled={measure === "average"}
              onChange={(event) => onView(event.target.value)}
            >
              <option value="absolute">Absolutte tall</option>
              <option value="percent">Andel (%)</option>
            </Select>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-admin-text-subtle">
              Format{" "}
              <Definition text="Graf viser valgt måltall per måned. Tabell viser alle fire måltall per måned og segment. CSV følger tabellens sortering når tabellen er valgt, ellers alle segmentene bak grafen. Måneder uten fakturaer eksporteres som «Ingen fakturaer», måneder Periode ikke svarte for som «Mangler data» uten tall." />
            </span>
            <div
              id={`${id}-mode`}
              role="group"
              aria-label="Format"
              className="flex gap-1"
            >
              {(["chart", "table"] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={
                    (mode === "table" ? "table" : "chart") === option
                      ? "default"
                      : "outline"
                  }
                  aria-pressed={
                    (mode === "table" ? "table" : "chart") === option
                  }
                  onClick={() => onMode(option)}
                >
                  {option === "chart" ? "Graf" : "Tabell"}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {mode !== "table" && rows.length > 0 && (
          <p className="mb-3 text-xs text-admin-text-subtle">
            {measure === "average"
              ? "Tall for hver gruppe"
              : "Tall i hver farge · månedstotal over søylen"}{" "}
            <Definition
              text={
                measure === "average"
                  ? MEASURES.average.definition
                  : LABEL_DEFINITION
              }
            />
          </p>
        )}
        {measure === "average" && (
          <p className="mb-3 text-xs text-admin-text-subtle">
            Gjennomsnitt vises som separate søyler, ikke stablet.{" "}
            <Definition text={MEASURES.average.definition} />
          </p>
        )}
        {measure === "customers" && dimension === "product" && (
          <p className="mb-3 text-xs text-admin-text-subtle">
            Kunder med flere abonnementstyper telles i hver type.{" "}
            <Definition text={MEASURES.customers.definition} />
          </p>
        )}
        {!rows.length ? (
          <p className="text-sm text-admin-text-subtle">
            Ingen fakturaer i tilgjengelige måneder.
          </p>
        ) : mode === "table" ? (
          <DashboardTable
            table={table}
            columns={columns}
            renderRow={(row) => (
              <TableRow key={`${row.month}-${row.segment}`}>
                <TableCell>{formatMonth(row.month)}</TableCell>
                <TableCell>{row.label}</TableCell>
                <TableCell className="text-right">
                  {formatCurrency(row.totalAmount / 100)}
                </TableCell>
                <TableCell className="text-right">{row.invoiceCount}</TableCell>
                <TableCell className="text-right">
                  {row.customerCount}
                </TableCell>
                <TableCell className="text-right">
                  {measureValue(row, "average") === null
                    ? "—"
                    : formatCurrency(measureValue(row, "average")!)}
                </TableCell>
              </TableRow>
            )}
          />
        ) : (
          <div className="overflow-x-auto">
            <div
              style={{
                minWidth: Math.max(
                  720,
                  data.length *
                    (measure === "average"
                      ? Math.max(72, segments.length * 60)
                      : 72),
                ),
              }}
            >
              <ResponsiveContainer width="100%" height={380}>
                <ComposedChart
                  data={data}
                  margin={{ top: 28, right: 12, bottom: 4, left: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-admin-card-border)"
                  />
                  <XAxis
                    dataKey="month"
                    tickFormatter={formatMonth}
                    tick={axisStyle}
                  />
                  <YAxis
                    tick={axisStyle}
                    domain={percent ? [0, 100] : undefined}
                    tickFormatter={(value: number) =>
                      percent
                        ? `${value}%`
                        : value.toLocaleString("nb-NO", { notation: "compact" })
                    }
                  />
                  <Tooltip
                    position={{ y: 0 }}
                    wrapperStyle={{ zIndex: 10, pointerEvents: "auto" }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div
                          role="region"
                          aria-label="Prognosedetaljer"
                          tabIndex={0}
                          className="max-h-[min(320px,60vh)] w-[min(560px,calc(100vw-3rem))] overflow-y-auto rounded-md border border-admin-card-border bg-admin-card p-3 text-[11px] leading-tight text-admin-text-primary shadow-lg"
                        >
                          <p className="mb-2 font-semibold">{formatMonth(String(label))}</p>
                          <div className="grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2">
                            {payload.filter((entry) =>
                              entry.value != null && segments.some((segment) => segment.key === entry.dataKey),
                            ).map((entry) => {
                              const key = String(entry.dataKey);
                              const absolute = Number(entry.payload[`${key}Absolute`]);
                              const share = Number(entry.payload[`${key}Percent`]);
                              const absoluteText =
                                measure === "income" || measure === "average"
                                  ? formatCurrency(absolute)
                                  : absolute.toLocaleString("nb-NO");
                              const shareText = `${share.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} %`;
                              return (
                                <div key={key} className="flex min-w-0 items-start gap-2">
                                  <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: entry.color }} />
                                  <div className="min-w-0">
                                    <p className="break-words">{entry.name}</p>
                                    <p className="mt-0.5 text-admin-text-secondary tabular-nums">
                                      {measure === "average"
                                        ? absoluteText
                                        : percent
                                          ? `${formatValue(Number(entry.value))} (${absoluteText})`
                                          : `${absoluteText} (${shareText})`}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
                  {segments.map((segment, index) => (
                    <Bar
                      key={segment.segment}
                      dataKey={segment.key}
                      name={segment.label}
                      stackId={measure === "average" ? undefined : "income"}
                      fill={forecastColor(index)}
                    >
                      <LabelList
                        dataKey={segment.key}
                        content={renderSegmentLabel}
                      />
                    </Bar>
                  ))}
                  {measure !== "average" && (
                    <Line
                      dataKey="stackHeight"
                      stroke="none"
                      dot={false}
                      activeDot={false}
                      legendType="none"
                      tooltipType="none"
                      isAnimationActive={false}
                    >
                      <LabelList
                        dataKey="stackTotal"
                        position="top"
                        formatter={(value) =>
                          typeof value === "number" ? formatTotal(value) : ""
                        }
                        fontSize={11}
                        fontWeight={600}
                        fill="var(--color-admin-text-primary)"
                      />
                    </Line>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
